import React, {
    useEffect,
    useState,
    useMemo,
    useCallback,
  } from "react";
  import {
    Col,
    Container,
    Row,
    Card,
    CardBody,
    Input,
    Button,
    Modal,
    ModalBody,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
  } from "reactstrap";
  
  import { useDispatch, useSelector } from "react-redux";
  import { createSelector } from "reselect";
  import { Link } from "react-router-dom";
  
  import BreadCrumb from "Components/Common/BreadCrumb";
  import Loader from "Components/Common/Loader";
  import { useFormik } from "formik";
  import * as Yup from "yup";
  import { toast } from "react-toastify";
  
  import type { RootState } from "store";
  import type { ThunkDispatch } from "redux-thunk";
  import type { AnyAction } from "redux";
  
  import {
    fetchDeliveryCharges,
    createDeliveryCharge,
    editDeliveryCharge,
    updateDeliveryChargeStatus,
  } from "slices/deliverycharge/thunk"; // <- adjust path if needed
  
import DeliveryChargesContainer from "./deliverychargecontainer";
import DeliveryChargeForm from "./deliverychargeform";

  
  // -------------------- SELECTOR --------------------
const selectDeliveryState = (state: RootState) => (state as any).doorstepdelivery; // slice key from rootReducer
  
  const deliverySelector = createSelector(selectDeliveryState, (delivery) => ({
    loading: delivery.loading,
    pagination: delivery.pagination,
    charges: delivery.charges || [], // API Response: data: [...]
  lastCreatedChargeId: delivery.lastCreatedChargeId ?? null,
  }));
  
  // -------------------- COMPONENT --------------------
  const DeliveryChargesPage: React.FC = () => {
    const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
    const { charges, loading, pagination } = useSelector(deliverySelector);
  
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editChargeData, setEditChargeData] = useState<any>(null);
  
    // -------- Load charges first time --------
    useEffect(() => {
      dispatch(fetchDeliveryCharges());
    }, [dispatch]);
  
    // When search changes, reset to first page
    useEffect(() => {
      setPage(1);
    }, [searchQuery]);
  
    // helper: allow up to 3 decimal places
    const allowUpTo3Decimals = (value: string) => {
      if (value === undefined || value === null || value === "") return false;
      return /^\d+(\.\d{1,3})?$/.test(String(value).trim());
    };

    // -------- Formik for Add / Edit --------
    const validation = useFormik({
      initialValues: {
        slotid: "",
        min_amount: "",
        max_amount: "",
        chage_type: "",
        charge_value: "",
        status: "Active",
      },
      validationSchema: Yup.object({
        slotid: Yup.string().trim().required("Time slot is required"),
        min_amount: Yup.string().trim().nullable(),
        max_amount: Yup.string().trim().nullable(),
        chage_type: Yup.string().trim().required("Charge type is required"),
        charge_value: Yup.string()
          .trim()
          .required("Value is required")
          .test(
            "charge-value-number",
            "Value must be a valid number",
            (val) =>
              val !== undefined &&
              val !== null &&
              val !== "" &&
              !isNaN(Number(val))
          )
          .test(
            "charge-value-decimals",
            "Maximum 3 decimal places allowed",
            (val) =>
              val !== undefined &&
              val !== null &&
              val !== "" &&
              allowUpTo3Decimals(val as string)
          ),
        status: Yup.string().trim().required("Status is required"),
      }),
      onSubmit: async (values, { resetForm }) => {
        try {
          let res: any;
          const toNumberOrDefault = (val: any, fallback: number) => {
            if (val === undefined || val === null || String(val).trim() === "") {
              return fallback;
            }
            const num = Number(val);
            return Number.isNaN(num) ? fallback : num;
          };
          const payload = {
            ...values,
            slotid: values.slotid ? Number(values.slotid) : "",
            min_amount: toNumberOrDefault(values.min_amount, 1),
            max_amount: toNumberOrDefault(values.max_amount, 500000),
            chage_type: values.chage_type,
            charge_value:
              values.charge_value !== "" ? Number(values.charge_value) : "",
          };
          if (isEdit && editChargeData) {
            res = await dispatch(
              editDeliveryCharge(editChargeData.id, payload) as any
            );
            if (!res?.success) {
              toast.error(res?.message || "Failed to update delivery charge");
              return;
            }
            toast.success("Delivery charge updated successfully!");
            setIsModalOpen(false);
          } else {
            res = await dispatch(createDeliveryCharge(payload) as any);
            if (!res?.success) {
              toast.error(res?.message || "Failed to create delivery charge");
              return;
            }
            toast.success(
              res?.data
                ? `Charge has been saved successfully`
                : "Charge has been saved successfully"
            );
            setIsModalOpen(false);
          }
  
          await dispatch(fetchDeliveryCharges() as any);
  
          resetForm();
          setIsEdit(false);
          setEditChargeData(null);
        } catch (err) {
          console.error("Error saving delivery charge:", err);
        }
      },
    });
  
    const toggleModal = () => {
      if (!isModalOpen) {
        validation.resetForm();
        setIsEdit(false);
        setEditChargeData(null);
      }
      setIsModalOpen(!isModalOpen);
    };
  
    const handleRowClick = useCallback((row: any) => {
      // optional row click behaviour
    }, []);
  
    const handleEditClick = useCallback(
      (row: any) => {
        setIsEdit(true);
        setEditChargeData(row);
        setIsModalOpen(true);
  
        validation.setValues({
          slotid: row.slotid ?? "",
          min_amount: row.min_amount ?? "",
          max_amount: row.max_amount ?? "",
          chage_type: row.chage_type || row.chage || row.charge_type || "",
          charge_value: row.charge_value ?? "",
          status: row.status || "Active",
        });
      },
      [validation]
    );
  
    // -------- Inline status change --------
    const handleStatusChange = useCallback(
      async (row: any, newStatus: string) => {
        if (row.status === newStatus) return;
  
        const res: any = await dispatch(
          updateDeliveryChargeStatus(row.id, { status: newStatus }) as any
        );
  
        if (res?.success) {
          toast.success("Status updated successfully");
          dispatch(fetchDeliveryCharges() as any);
        } else {
          toast.error(res?.message || "Failed to update status");
        }
      },
      [dispatch]
    );
  
    // -------- Client-side search filter --------
    const filteredCharges = useMemo(() => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return charges;
  
      return charges.filter((c: any) => {
        return (
          c?.time_slot?.toLowerCase().includes(q) ||
          c?.slotname?.toLowerCase().includes(q) ||
          String(c?.min_amount ?? "").toLowerCase().includes(q) ||
          String(c?.max_amount ?? "").toLowerCase().includes(q) ||
          c?.chage_type?.toLowerCase().includes(q) ||
          c?.chage?.toLowerCase().includes(q) ||
          c?.charge_type?.toLowerCase().includes(q)
        );
      });
    }, [charges, searchQuery]);
  
    // -------- Columns --------
    const columns = useMemo(
      () => [
        {
          header: "Charges ID",
          accessorKey: "id",
          cell: ({ row }: any) => {
            const id = row.original.id;
            return `#ID${String(id ?? "").padStart(4, "0")}`;
          },
        },
        {
          header: "Time slot",
          accessorKey: "slotname",
          cell: ({ row }: any) => row.original.slotname || row.original.time_slot || "—",
        },
        {
          header: "Min. Amount",
          accessorKey: "min_amount",
          cell: (cell: any) => {
            const value = cell.getValue();
            return value !== undefined && value !== null ? `BD ${value}` : "—";
          },
        },
        {
          header: "Max. Amount",
          accessorKey: "max_amount",
          cell: (cell: any) => {
            const value = cell.getValue();
            return value !== undefined && value !== null ? `BD ${value}` : "—";
          },
        },
        {
          header: "Charge type",
          accessorKey: "chage_type",
          cell: ({ row }: any) =>
            row.original.chage_type ||
            row.original.chage ||
            row.original.charge_type ||
            "—",
        },
        {
          header: "Value",
          accessorKey: "charge_value",
          cell: ({ row }: any) => {
            const value = row.original.charge_value;
            const type = row.original.charge_type;
            if (value === undefined || value === null) return "—";
            if (type?.toLowerCase() === "percentage") return `${value} %`;
            return `BD ${value}`;
          },
        },
        {
          header: "Status",
          accessorKey: "status",
          enableColumnFilter: false,
          cell: ({ row }: any) => {
            const currentStatus = row.original.status || "Active";
            const isActive = currentStatus.toLowerCase() === "active";
  
            const borderColor = isActive ? "#159E3A" : "#A65F00";
            const textColor = borderColor;
            const bgColor = "transparent";
  
            return (
              <UncontrolledDropdown>
                <DropdownToggle
                  caret
                  className="btn btn-sm d-inline-flex align-items-center"
                  style={{
                    borderRadius: "20px",
                    padding: "4px 12px",
                    fontSize: "12px",
                    fontWeight: 500,
                    border: `1px solid ${borderColor}`,
                    color: textColor,
                    backgroundColor: bgColor,
                    textTransform: "capitalize",
                  }}
                >
                  {currentStatus}
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem
                    onClick={() => handleStatusChange(row.original, "Active")}
                  >
                    Active
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => handleStatusChange(row.original, "Inactive")}
                  >
                    Inactive
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            );
          },
        },
        {
          header: "Actions",
          accessorKey: "actions",
          cell: (cellProps: any) => {
            const row = cellProps.row.original;
            return (
              <ul className="list-inline hstack gap-1 mb-0">
                <li className="list-inline-item" title="Edit">
                  <Button
                    color=""
                    className="btn btn-icon btn-soft-primary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick(row);
                    }}
                  >
                    <i className="ri-pencil-fill" />
                  </Button>
                </li>
              </ul>
            );
          },
        },
      ],
      [handleEditClick, handleStatusChange]
    );
  
    // -------- Render --------
    return (
      <div className="page-content">
        <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
          <BreadCrumb
            title="Doorstep Delivery Charges"
            pageTitle={["Delivery", "Doorstep delivery charges"]}
          />
  
          <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
            <Col md={3}>
              {/* <div className="position-relative">
                <Input
                  type="text"
                  className="form-control border ps-5 ms-2 rounded-3"
                  value={searchQuery}
                  placeholder="Search..."
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <i
                  className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                  style={{ fontSize: "1.1rem" }}
                />
              </div> */}
            </Col>
  
            <Col
              md={9}
              className="text-end d-flex justify-content-end gap-2 align-items-center"
            >
              <Button color="primary" outline tag={Link} to="/doorstepdelivery">
                <i className="ri-truck-line align-middle me-1" />
                View Requests
              </Button>
              <Button color="primary" onClick={toggleModal}>
                + Add New Charges
              </Button>
            </Col>
          </Row>
  
          <Row>
            <Col lg={12}>
              <Card className="border-0 shadow-none">
                <CardBody>
                  {!loading ? (
                    filteredCharges.length > 0 ? (
                      <DeliveryChargesContainer
                        columns={columns}
                        data={filteredCharges}
                        pagination={pagination}
                        limit={limit}
                        setLimit={setLimit}
                        setPage={setPage}
                        onPageChange={(p: number) => setPage(p)}
                        onRowClick={handleRowClick}
                        tableClass="align-middle border-bottom rounded-pill"
                        theadClass="bg-primary text-white border-top border-success"
                        trClass=""
                        divClass="table-responsive table-card"
                      />
                    ) : (
                      <div className="text-center p-4">No records found.</div>
                    )
                  ) : (
                    <Loader />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
  
        <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
          <ModalBody>
            <DeliveryChargeForm
              validation={validation}
              isEdit={isEdit}
              charges={charges}
            />
          </ModalBody>
        </Modal>
      </div>
    );
  };
  
  export default DeliveryChargesPage;
  